import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsTouchBand } from '@/hooks/useBreakpoint';
import type { SelectOption } from '@/types/common.types';
import { useFieldLocked } from './FieldLock';

/**
 * Keeps the panel off the viewport edge. Was an inline 8 in three places; named so
 * the phone branch below cannot drift from the desktop arithmetic it must preserve.
 */
const MENU_GUTTER = 8;

/**
 * Three 44px rows. Below `md` the panel is capped to the space the on-screen
 * keyboard leaves behind, and a cap tighter than this is worse than an overhang.
 */
const MIN_MENU_HEIGHT = 132;

interface MenuPosition {
  top: number;
  left: number;
  width: number;
  /** Below `md` only. Undefined above it, where the list keeps its `max-h-72`. */
  maxHeight?: number;
}

export interface SelectProps<T = string> {
  label?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  placeholder?: string;
  options: SelectOption<T>[];
  value?: T | null;
  onChange?: (value: T | null) => void;
  /** Allow clearing the selection */
  clearable?: boolean;
  /** Enable search filtering */
  searchable?: boolean;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  wrapperClassName?: string;
  id?: string;
  name?: string;
  renderOption?: (option: SelectOption<T>) => React.ReactNode;
  /**
   * How the chosen option is drawn in the closed field; absent = its label.
   * For options that are more than text (a logo, a swatch), so the field shows
   * what was picked the way the list did.
   */
  renderValue?: (option: SelectOption<T>) => React.ReactNode;
}

export function Select<T = string>({
  label,
  error,
  hint,
  required,
  placeholder = 'Select…',
  options,
  value,
  onChange,
  clearable = false,
  searchable = false,
  disabled = false,
  loading = false,
  className,
  wrapperClassName,
  id,
  name,
  renderOption,
  renderValue,
}: SelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [menuPos, setMenuPos] = useState<MenuPosition | null>(null);

  /**
   * The one gate for every touch-only branch in this file. Desktop output — rendered
   * DOM, listener set, positioning arithmetic and re-render sequence — is identical
   * to what it was before any of this was added; each branch below keeps the old
   * expression verbatim on its `false` side.
   *
   * The band, not `useIsBelow('md')`. Every behaviour gated here is a response to a
   * finger — iOS raising a keyboard, a tap carrying two pixels of drag — and none of
   * it is a response to available width. Scoped below `md` it switched off between
   * 768px and 1023.98px, which is iPad portrait and every iPhone in landscape
   * (844px on a 14, 932px on a Pro Max), and SCRUM-1380 is what that gap reads like
   * from the outside: a search field with a focus ring and no keyboard, a panel
   * parked left of its own field, and a dropdown closing on the tap meant to open
   * it. `useIsBelow('lg')` is not the fix either — it would hand all of this to a
   * mouse user with a narrow window, which is what `index.css` rejected `max-lg:`
   * for. The pointer axis is what separates a device from a window.
   *
   * Routing through the shared hook rather than a local media query is what makes
   * the `?forceDesktopLayout=1` kill switch turn all of it off without a redeploy.
   */
  const isTouch = useIsTouchBand();

  /**
   * Inside a locked FieldLockScope the field shows its value as plain content: it
   * never opens, has no chevron or clear, and is not drawn disabled.
   */
  const locked = useFieldLocked();

  const inputId = id ?? (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
  const errorId = error ? `${inputId}-error` : undefined;

  const selectedOption = options.find((o) => o.value === value);

  const filteredOptions = searchable
    ? options.filter((o) =>
        // `searchText` when the option carries one, so an option whose visible
        // label is a bare code (`+41`) is still findable by its name.
        String(o.searchText ?? o.label ?? '').toLowerCase().includes(search.toLowerCase()),
      )
    : options;

  const handleSelect = useCallback(
    (opt: SelectOption<T>) => {
      if (opt.disabled) return;
      onChange?.(opt.value);
      setOpen(false);
      setSearch('');
    },
    [onChange],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange?.(null);
    },
    [onChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Only swallow Escape while the menu is open, and stop it there so it
        // never reaches an enclosing Modal's document-level Escape handler
        // (which would close the whole dialog instead of just this dropdown).
        if (open) {
          e.stopPropagation();
          setOpen(false);
        }
      } else if (e.key === 'Enter' || e.key === ' ') {
        if (!open) setOpen(true);
      }
    },
    [open],
  );

  /**
   * Measure the trigger and place the portaled panel against it, flipping above when
   * there is not enough room below.
   *
   * Hoisted out of the layout effect so the scroll and visual-viewport handlers can
   * re-run it: below `md` the panel follows its field instead of closing, which needs
   * a measurement per frame rather than one per open.
   *
   * Two invariants, and `react-hooks/exhaustive-deps` is off in this repo so neither
   * is enforced by lint. First, the dependency list stays `[isTouch]`: this must read
   * only refs, `window` and that flag. Reading `search` would give it a new identity
   * on every keystroke and tear down every listener below once per character. The
   * height it needs comes from the live DOM via `menuRef`, which is current no matter
   * how old the closure is. Second, it must never be invoked from a render — the
   * bail-out below is a safety net, not a licence.
   */
  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight ?? 0;

    // Two viewports, because there are two questions and they have different answers
    // once a keyboard is up (SCRUM-1380, third report).
    //
    // `visualViewport` is the only source that shrinks for the on-screen keyboard;
    // `innerHeight` does not, so a panel *sized* from it hides behind the keyboard.
    // Read inside the touch band only — on desktop it also diverges from
    // `innerHeight` under pinch zoom, and letting that reach this arithmetic would
    // be a desktop change. With `vv` null every expression below collapses to the
    // original one.
    const vv = isTouch ? window.visualViewport : null;
    const viewTop = vv ? vv.offsetTop : 0;
    const viewBottom = viewTop + (vv ? vv.height : window.innerHeight);

    // The *layout* viewport, which is what "has the anchor scrolled off the page"
    // has to be measured against. It does not shrink for the keyboard, and that is
    // the whole reason it is a second quantity rather than the one above.
    const pageBottom = window.innerHeight;

    // Because the touch panel tracks the page instead of closing with it, it has to
    // close itself once the field it belongs to has scrolled off screen — otherwise
    // it outlives its anchor and floats over unrelated content.
    //
    // Against `viewBottom` this dismissed a panel the user was typing into. A ~300px
    // keyboard on a 667px screen leaves a 340px band, so *any* trigger below y≈340
    // satisfied the test with no scrolling at all — and Plan Setup is the last card
    // on a long form, so its fields are precisely the ones that sit low. Filtering
    // re-measures by design, which is why the report reads "when types": every
    // keystroke re-ran this check. Occluded is not gone; only the layout viewport
    // can tell the two apart.
    if (isTouch && (r.bottom < 0 || r.top > pageBottom)) {
      setOpen(false);
      setSearch('');
      return;
    }

    const spaceBelow = viewBottom - r.bottom - MENU_GUTTER;
    const spaceAbove = r.top - viewTop - MENU_GUTTER;

    // The flip decision and the cap now come from one branch. They used to be
    // computed independently, and `maxHeight` took `max(spaceBelow, spaceAbove)`
    // regardless of which side `top` had chosen — so a panel placed below with 20px
    // under it could be handed the 250px cap from above and paint straight through
    // the keyboard.
    let top: number;
    let space: number;
    if (!menuHeight || spaceBelow >= menuHeight) {
      top = r.bottom + 4;
      space = spaceBelow;
    } else if (spaceAbove > spaceBelow && menuHeight <= spaceAbove) {
      top = r.top - menuHeight - 4;
      space = spaceAbove;
    } else {
      // Neither side fits. Below is the friendlier overhang, and the clamp below
      // keeps it inside the band.
      top = r.bottom + 4;
      space = spaceBelow;
    }

    let left = r.left;
    // In the touch band the panel carries an explicit width equal to the trigger
    // (see the style block), so there is nothing to measure and the clamp is inert.
    // Consulting `offsetWidth` here is what used to move it: the panel is
    // shrink-to-fit between `minWidth` and `maxWidth`, so on its first commit — at
    // `left: 0` — it measures against the whole viewport and returns the 90vw cap.
    // That inflated width then fed the right-edge clamp and pushed `left` to the
    // left of the field the panel was supposed to be anchored to.
    const menuWidth = isTouch
      ? r.width
      : Math.max(r.width, menuRef.current?.offsetWidth ?? r.width);
    if (left + menuWidth > window.innerWidth - MENU_GUTTER) {
      left = window.innerWidth - menuWidth - MENU_GUTTER;
    }
    if (left < MENU_GUTTER) left = MENU_GUTTER;

    // The band itself is the ceiling. `spaceAbove`/`spaceBelow` are measured from the
    // anchor, and when the keyboard has left the anchor outside the visible band those
    // measurements run straight through it — a trigger at y=400 against a 340px band
    // reports 392px "above" it, most of which is keyboard. Clamping to the band is
    // what stops a long list overhanging no matter where its field ended up.
    const bandHeight = viewBottom - viewTop - MENU_GUTTER * 2;
    const maxHeight = isTouch
      ? Math.max(MIN_MENU_HEIGHT, Math.min(space, bandHeight))
      : undefined;

    // Keep the panel inside the band the keyboard left, even when the anchor is not.
    // Without this the fallback above parks it at `r.bottom + 4`, which for a trigger
    // the keyboard is covering is off the bottom of what the user can see — the same
    // "the list disappeared" as a dismissal, arrived at by a different route.
    //
    // Upper bound first, so a band shorter than the panel pins to the top of the band
    // rather than inverting. `maxHeight` is the panel's rendered height once it takes
    // effect, so it — not the pre-cap measurement — is what has to fit.
    if (isTouch && maxHeight !== undefined) {
      const height = Math.min(menuHeight || maxHeight, maxHeight);
      top = Math.min(top, viewBottom - height - MENU_GUTTER);
      top = Math.max(top, viewTop + MENU_GUTTER);
    }

    setMenuPos((prev) => {
      // Desktop measures once per open and never repositions, so it keeps the plain
      // assignment — an equality bail-out here would alter its re-render sequence for
      // no benefit. On a phone this runs on every scroll and viewport frame, where an
      // unchanged measurement must not produce a new object and another render.
      if (!isTouch) return { top, left, width: r.width, maxHeight };
      if (
        prev &&
        prev.top === top &&
        prev.left === left &&
        prev.width === r.width &&
        prev.maxHeight === maxHeight
      ) {
        return prev;
      }
      return { top, left, width: r.width, maxHeight };
    });
  }, [isTouch]);

  /** Coalesces a burst of scroll/viewport events into one measurement per frame. */
  const rafRef = useRef<number | null>(null);
  const requestReposition = useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      updatePosition();
    });
  }, [updatePosition]);

  useEffect(
    () => () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    },
    [],
  );

  // Position the portaled dropdown relative to the trigger.
  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    // The second pass is still required: the panel's own height is not final on the
    // first one, and the flip decision depends on it.
    updatePosition();
    const raf = requestAnimationFrame(updatePosition);
    window.addEventListener('resize', updatePosition);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  // Scroll in any ancestor (but not the dropdown's own scrollable list).
  useEffect(() => {
    if (!open) return;
    function onScroll(e: Event) {
      if (menuRef.current?.contains(e.target as Node)) return;
      // On a touch screen a scroll is usually not the user dismissing anything: iOS
      // scrolls a focused input into view when it raises the keyboard, and a tap with
      // two pixels of drag is a scroll. Closing on either is what shut the panel "on
      // some taps", and it would also have closed it the instant the keyboard fix
      // started working. Follow the field instead.
      if (isTouch) {
        requestReposition();
        return;
      }
      setOpen(false);
      setSearch('');
    }
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open, isTouch, requestReposition]);

  // The keyboard's arrival resizes the visual viewport; it does not fire a window
  // `resize`. Touch-band only, so desktop's listener set is untouched.
  useEffect(() => {
    if (!open || !isTouch) return;
    const vv = window.visualViewport;
    if (!vv) return; // jsdom, and any browser that does not implement it
    vv.addEventListener('resize', requestReposition);
    vv.addEventListener('scroll', requestReposition);
    return () => {
      vv.removeEventListener('resize', requestReposition);
      vv.removeEventListener('scroll', requestReposition);
    };
  }, [open, isTouch, requestReposition]);

  // Filtering changes the panel's height, and a panel flipped above its trigger is
  // bottom-anchored — without a re-measure it drifts off the field as the user types.
  useEffect(() => {
    if (!open || !isTouch) return;
    requestReposition();
  }, [search, open, isTouch, requestReposition]);

  // Close on outside click — both trigger and menu count as "inside".
  useEffect(() => {
    if (!open) return;
    function onOutside(e: Event) {
      const t = e.target as Node;
      if (containerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
      setSearch('');
    }
    // Below `md`, `pointerdown`: iOS Safari dispatches no mouse event at all for a
    // tap on a non-interactive element, which leaves the panel undismissable on a
    // phone. Desktop deliberately stays on `mousedown` — mouse input does fire
    // `pointerdown` first on the same target, so a swap would be safe, but it would
    // still be a change to a desktop code path.
    const event = isTouch ? 'pointerdown' : 'mousedown';
    document.addEventListener(event, onOutside);
    return () => document.removeEventListener(event, onOutside);
  }, [open, isTouch]);

  // Focus search when dropdown opens
  useEffect(() => {
    // On a touch device the field is deliberately left unfocused, so that the user's
    // own tap becomes the focus event — the one iOS honours by raising the keyboard.
    // Focusing it here gave the field a focus ring and no keyboard, and then the tap
    // landed on an already-focused input and changed nothing, which is the reported
    // bug. Same reasoning as `sheet.tsx`'s `onOpenAutoFocus`. Note that winning the
    // race is not an option either: the panel's first commit is `visibility: hidden`,
    // and `focus()` on a hidden element is a no-op.
    //
    // The gate is the touch band rather than a width, because a keyboard is raised by
    // a finger and not by a narrow window. Below `md` this left the deferred autofocus
    // firing on every tablet and every phone in landscape.
    if (isTouch) return;
    if (open && searchable) {
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [open, searchable, isTouch]);

  return (
    <div className={cn('w-full', wrapperClassName)} ref={containerRef}>
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-slate-700 mb-1.5">
          {label}
          {required && (
            <span className="ml-0.5 text-red-500" aria-hidden="true">
              {' *'}
            </span>
          )}
        </label>
      )}

      {/* Hidden native select for form submission */}
      {name && (
        <input
          type="hidden"
          name={name}
          value={value !== undefined && value !== null ? String(value) : ''}
        />
      )}

      <div className="relative">
        <button
          ref={triggerRef}
          type="button"
          id={inputId}
          disabled={!locked && (disabled || loading)}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-invalid={!!error}
          aria-describedby={errorId}
          onClick={() => !locked && !disabled && !loading && setOpen((o) => !o)}
          onKeyDown={locked ? undefined : handleKeyDown}
          className={cn(
            'w-full flex items-center gap-2 text-left',
            locked ? 'form-input-base cursor-default' : 'form-input-base cursor-pointer',
            error && 'form-input-error',
            !locked && (disabled || loading) && 'cursor-not-allowed',
            className,
          )}
        >
          <span className={cn('flex-1 truncate', !selectedOption && 'text-slate-400')}>
            {loading
              ? 'Loading…'
              : selectedOption
                ? (renderValue ? renderValue(selectedOption) : selectedOption.label)
                : placeholder}
          </span>

          {!locked && (
            <span className="flex items-center gap-1 flex-shrink-0">
              {clearable && selectedOption && (
                <X
                  className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600"
                  onClick={handleClear}
                />
              )}
              <ChevronDown
                className={cn(
                  'h-4 w-4 text-slate-400 transition-transform duration-150',
                  open && 'rotate-180',
                )}
              />
            </span>
          )}
        </button>

        {open && typeof document !== 'undefined' && createPortal(
          <div
            ref={menuRef}
            role="listbox"
            aria-label={label}
            style={{
              position: 'fixed',
              top: menuPos?.top ?? -9999,
              left: menuPos?.left ?? 0,
              // In the touch band the panel is pinned to the width of the field it
              // belongs to. Left to shrink-to-fit between min and max, its width is a
              // function of `left` while `left` is computed from its width, and the
              // two chase each other: the first commit measures at `left: 0`, gets
              // the 90vw cap, and the right-edge clamp then parks the panel left of
              // its own field and hanging past the card. An explicit width ends it.
              ...(isTouch
                ? { width: menuPos?.width, maxWidth: 'none' }
                // As corp: as wide as the longest option (never narrower than the
                // field); an option wraps only past the screen's width.
                : { minWidth: menuPos?.width ?? 'auto', maxWidth: '90vw' }),
              // Touch-only; undefined on desktop, where the list keeps `max-h-72`.
              maxHeight: menuPos?.maxHeight,
              zIndex: 1000,
              visibility: menuPos ? 'visible' : 'hidden',
            }}
            className={cn(
              'rounded-md bg-white border border-slate-200 shadow-xl',
              'animate-scale-in origin-top',
              // Lets the list flex inside the measured cap above rather than
              // overflowing it. Driven by the same boolean as `maxHeight` on purpose:
              // a Tailwind variant cannot express the pointer axis at all, so it would
              // apply to a narrow desktop window where `maxHeight` is undefined, and
              // the list would flex with no cap above it and grow unbounded.
              isTouch && 'flex flex-col overflow-hidden',
            )}
          >
            {searchable && (
              <div className="p-2 border-b border-slate-100">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <input
                    ref={searchRef}
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search…"
                    // No font-size floor here: the iOS auto-zoom rule at the bottom of
                    // `index.css` (SCRUM-1373) already lifts every text input to 16px,
                    // and it does so on `pointer: coarse` up to 1023.98px — which also
                    // covers an iPhone in landscape, where a `max-md:` class would miss.
                    // Adding one here would lose to it on touch and, worse, fire for a
                    // mouse user in a narrow window, which that rule deliberately spares.
                    // The 44px floor is R13, and it also widens a target that otherwise
                    // sits 13px under a trigger that closes the panel when hit.
                    className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 max-md:min-h-[44px]"
                  />
                </div>
              </div>
            )}

            <div className={cn('overflow-y-auto py-1', isTouch ? 'flex-1 min-h-0' : 'max-h-72')}>
              {filteredOptions.length === 0 ? (
                <p className="px-3 py-2 text-sm text-slate-400 text-center">No options found</p>
              ) : (
                filteredOptions.map((opt) => {
                  const isSelected = opt.value === value;
                  return (
                    <button
                      key={String(opt.value)}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      disabled={opt.disabled}
                      onClick={() => handleSelect(opt)}
                      className={cn(
                        'w-full flex items-start gap-2 px-3 py-2 text-sm text-left',
                        // R13's 44px floor below `md` only; desktop density unchanged.
                        'max-md:min-h-[44px]',
                        'hover:bg-slate-50 transition-colors',
                        isSelected && 'bg-blue-50 text-blue-700',
                        opt.disabled && 'opacity-50 cursor-not-allowed',
                      )}
                    >
                      <span className="flex-1 break-words whitespace-normal leading-snug">{renderOption ? renderOption(opt) : opt.label}</span>
                      {isSelected && <Check className="h-4 w-4 flex-shrink-0 mt-0.5" />}
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}
      </div>

      {hint && !error && <p className="mt-1.5 text-xs text-slate-500">{hint}</p>}
      {error && (
        <p id={errorId} role="alert" className="mt-1.5 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
